/**
 * UploadProgressPanel — Perceived Speed System (Feb 7 2026 P0).
 *
 * Single presentation component that owns the entire "feel-fast"
 * UX layer for chunked uploads. Designed so any uploader (lesson
 * review, clip basket, future) can drop it in and immediately get
 * the 7-part spec:
 *
 *   1. Instant feedback (<300ms after file pick)
 *   2. Optimising stage (1-2s minimum, even if real prep is fast)
 *   3. Smoothed progress with creep-when-stalled
 *   4. Pause/resume with auto-retry countdown (no panic)
 *   5. Finalising hold (>= 1.5s)
 *   6. Success moment (subtle animation, 1.2s)
 *   7. Rotating reassurance messages every 12-14s
 *
 * Phases (controlled externally; the panel handles all visual logic):
 *
 *   "idle"        → nothing rendered (panel hidden)
 *   "preparing"   → instant skeleton + "Preparing your upload…"
 *   "optimising"  → "Optimising video for faster upload…"
 *   "uploading"   → smoothed bar + rotating messages
 *   "paused"      → friendly copy, auto-retry countdown
 *   "finalising"  → "Finalising your upload…" (held >= 1.5s)
 *   "complete"    → "Upload complete" + subtle check-in animation
 *   "error"       → error message + retry/cancel
 *
 * Owner directive (verbatim): "If user thinks 'this is taking ages',
 * the system has failed. If user thinks 'this is moving nicely',
 * the system has succeeded."
 */
import { useEffect, useRef, useState } from "react";
import {
  Loader2, CheckCircle2, AlertCircle, RotateCw, X, Sparkles,
} from "lucide-react";

const ROTATING_MESSAGES = [
  "Uploading your lesson…",
  "Processing video data…",
  "Almost there…",
  "Finalising upload…",
];

const ROTATE_EVERY_MS = 13000;       // rotate every ~13s
const STALL_DETECT_MS = 2000;        // creep kicks in after 2s of no real progress
const CREEP_PER_TICK = 0.18;         // % per tick (~250ms) — gentle drift
const CREEP_TICK_MS = 250;
const CREEP_MAX_AHEAD = 1.4;         // never drift more than +1.4% past real
const FINALISING_MIN_MS = 1500;      // minimum hold on "Finalising"
const COMPLETE_FLASH_MS = 1200;      // success-moment animation length
const AUTO_RETRY_DEFAULT_MS = 25000; // auto-retry paused after 25s

function formatBytes(n) {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(v >= 100 || u === 0 ? 0 : 1)} ${units[u]}`;
}

export default function UploadProgressPanel({
  phase = "idle",
  pct = 0,
  fileName = "",
  fileSize = 0,
  pausedReason = null,        // "auth" | "network" | null
  pausedSafe = null,          // {received, total} for "X / Y chunks safe" copy
  onResume,                   // tap-to-resume handler
  onCancel,                   // user cancel handler
  // Hotfix Feb 8, 2026 — when an upload hits the generic "error"
  // phase, the resume handler may have nothing to resume from
  // (paused-upload state was never set, e.g. finalize failure).
  // The button label must reflect what the click will actually do
  // — silent no-ops cost user trust. `canResume = false` switches
  // the error button label from "Try again" to "Start again", and
  // upstream the click handler reopens the file picker instead of
  // attempting a no-op resume.
  canResume = true,
  autoRetryMs = AUTO_RETRY_DEFAULT_MS,
  mbps = null,                // live upload speed in Mbps (helper-supplied)
  className = "",
  testIdPrefix = "upload",
}) {
  // ── Smoothed display pct (creeps when real pct stalls) ──────────
  const [displayedPct, setDisplayedPct] = useState(0);
  const lastRealRef = useRef({ pct: 0, at: Date.now() });
  const prevPhaseRef = useRef(phase);

  // Reset smoothing only when leaving an active state into idle, OR
  // entering "preparing" from idle (a NEW upload). On resume — i.e.
  // entering "preparing" / "uploading" / "paused" with pct already
  // > 0 — we MUST NOT reset, otherwise the bar visibly rewinds to 0
  // and the user sees the upload "restart" from 0% even though the
  // server already has 42% of the chunks.
  useEffect(() => {
    const prevPhase = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (phase === "idle") {
      setDisplayedPct(0);
      lastRealRef.current = { pct: 0, at: Date.now() };
      return;
    }
    // Fresh upload: idle → preparing with no server-seeded pct.
    if (phase === "preparing" && prevPhase === "idle" && pct === 0) {
      setDisplayedPct(0);
      lastRealRef.current = { pct: 0, at: Date.now() };
      return;
    }
    // Resume / re-entry into uploading or paused with server-seeded
    // pct: SNAP displayedPct to truth instantly. One source of truth
    // is the server's received_chunks → pct, surfaced via the `pct`
    // prop. The bar must reflect that immediately.
    if ((phase === "uploading" || phase === "paused" || phase === "preparing")
        && pct > 0) {
      setDisplayedPct((prev) => (pct > prev ? pct : prev));
      lastRealRef.current = { pct, at: Date.now() };
      if (typeof console !== "undefined" && console.info) {
        console.info(
          `[UploadProgressPanel] phase=${phase} (prev=${prevPhase}) snapping displayedPct to server-truth pct=${pct}.`,
        );
      }
    }
  }, [phase, pct]);

  // Track real-pct changes so the creep can detect stalls.
  useEffect(() => {
    if (pct > lastRealRef.current.pct) {
      lastRealRef.current = { pct, at: Date.now() };
    }
  }, [pct]);

  // Smoothing tick — only active during uploading.
  useEffect(() => {
    if (phase !== "uploading") return undefined;
    const id = setInterval(() => {
      setDisplayedPct((prev) => {
        const real = pct;
        // Server truth is the floor — never display below it.
        if (real > prev) {
          // Catch up fast when real jumps ahead by a meaningful gap.
          if (real - prev > 5) return Math.min(99.5, real);
          return Math.min(99.5, prev + Math.max(0.6, (real - prev) * 0.25));
        }
        // Real has stalled — gently creep, capped.
        const stalledMs = Date.now() - lastRealRef.current.at;
        if (stalledMs > STALL_DETECT_MS && prev < real + CREEP_MAX_AHEAD) {
          return Math.min(99.5, prev + CREEP_PER_TICK);
        }
        return prev;
      });
    }, CREEP_TICK_MS);
    return () => clearInterval(id);
  }, [phase, pct]);

  // Snap to 100 once we hit finalising / complete.
  useEffect(() => {
    if (phase === "finalising" || phase === "complete") setDisplayedPct(100);
  }, [phase]);

  // Slow-connection hint — if mbps stays below threshold during
  // active uploading for >= 8s, surface a friendly copy line so the
  // user understands the upload is working but the connection is
  // the bottleneck (vs "the app is broken"). Owner spec §"if upload
  // speed is poor, show 'Your connection is slow' rather than
  // looking broken".
  const SLOW_MBPS_THRESHOLD = 2;
  const SLOW_HINT_AFTER_MS = 8000;
  const [showSlowHint, setShowSlowHint] = useState(false);
  const slowSinceRef = useRef(null);
  useEffect(() => {
    if (phase !== "uploading" || typeof mbps !== "number") {
      setShowSlowHint(false);
      slowSinceRef.current = null;
      return undefined;
    }
    if (mbps < SLOW_MBPS_THRESHOLD) {
      if (!slowSinceRef.current) slowSinceRef.current = Date.now();
      const elapsed = Date.now() - slowSinceRef.current;
      if (elapsed >= SLOW_HINT_AFTER_MS) setShowSlowHint(true);
      const id = setTimeout(
        () => setShowSlowHint(true),
        Math.max(0, SLOW_HINT_AFTER_MS - elapsed),
      );
      return () => clearTimeout(id);
    }
    setShowSlowHint(false);
    slowSinceRef.current = null;
    return undefined;
  }, [phase, mbps]);

  // ── Rotating reassurance messages (uploading only) ──────────────
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    if (phase !== "uploading") {
      setMsgIdx(0);
      return undefined;
    }
    const id = setInterval(
      () => setMsgIdx((i) => (i + 1) % ROTATING_MESSAGES.length),
      ROTATE_EVERY_MS,
    );
    return () => clearInterval(id);
  }, [phase]);

  // ── Auto-retry countdown when paused ────────────────────────────
  // Spec §4: don't immediately show error; auto-retry after ~20-30s.
  // pausedReason "auth" disables auto-retry — only the user can fix
  // a session expiry by signing in again.
  const [secsToRetry, setSecsToRetry] = useState(0);
  useEffect(() => {
    if (phase !== "paused" || pausedReason === "auth" || !onResume) {
      setSecsToRetry(0);
      return undefined;
    }
    const start = Date.now();
    const total = autoRetryMs;
    setSecsToRetry(Math.ceil(total / 1000));
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, total - elapsed);
      setSecsToRetry(Math.ceil(remaining / 1000));
      if (remaining <= 0) {
        clearInterval(tick);
        onResume?.();
      }
    }, 500);
    return () => clearInterval(tick);
  }, [phase, pausedReason, autoRetryMs, onResume]);

  if (phase === "idle") return null;

  // ── Headline copy per phase ─────────────────────────────────────
  const headline = (() => {
    switch (phase) {
      case "preparing":  return "Preparing your upload…";
      case "optimising": return "Optimising video for faster upload…";
      case "uploading":  return ROTATING_MESSAGES[msgIdx];
      case "paused":
        return pausedReason === "auth"
          ? "Sign in again to resume — your progress is saved."
          : "Connection paused — resuming…";
      case "finalising": return "Finalising your upload…";
      case "complete":   return "Upload complete";
      case "error":      return "Upload couldn't complete";
      default:           return "";
    }
  })();

  const subline = (() => {
    if (!fileName) return "";
    const sizeLabel = fileSize ? ` · ${formatBytes(fileSize)}` : "";
    return `${fileName}${sizeLabel}`;
  })();

  const showBar = ["preparing", "optimising", "uploading", "paused", "finalising", "complete"].includes(phase);
  const barPct = phase === "complete" || phase === "finalising"
    ? 100
    : Math.max(0, Math.min(100, displayedPct));

  const isPanelPaused = phase === "paused";
  const isComplete = phase === "complete";
  const isError = phase === "error";

  return (
    <div
      data-testid={`${testIdPrefix}-panel`}
      data-phase={phase}
      className={`rounded-2xl border p-4 ${
        isComplete
          ? "border-emerald-500/40 bg-emerald-500/[0.06]"
          : isError
          ? "border-rose-500/40 bg-rose-500/[0.06]"
          : isPanelPaused
          ? "border-amber-500/40 bg-amber-500/[0.06]"
          : "border-white/10 bg-white/[0.04]"
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-none">
          {isComplete ? (
            <CheckCircle2
              data-testid={`${testIdPrefix}-success-icon`}
              className="w-7 h-7 text-emerald-400 animate-[upload-success_1.2s_ease-out]"
            />
          ) : isError ? (
            <AlertCircle className="w-6 h-6 text-rose-400" />
          ) : isPanelPaused ? (
            <RotateCw className="w-5 h-5 text-amber-300 animate-spin-slow" />
          ) : phase === "preparing" || phase === "optimising" ? (
            <Sparkles className="w-5 h-5 text-indigo-300 animate-pulse" />
          ) : (
            <Loader2 className="w-5 h-5 text-indigo-300 animate-spin" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p
            data-testid={`${testIdPrefix}-headline`}
            className={`text-sm font-extrabold leading-tight ${
              isComplete ? "text-emerald-100"
                : isError ? "text-rose-100"
                : isPanelPaused ? "text-amber-100"
                : "text-white"
            }`}
          >
            {headline}
          </p>
          {subline && (
            <p
              data-testid={`${testIdPrefix}-subline`}
              className="text-[11px] text-zinc-400 mt-0.5 truncate"
            >
              {subline}
            </p>
          )}

          {showBar && (
            <div className="mt-3 h-1.5 rounded-full bg-white/8 overflow-hidden">
              <div
                data-testid={`${testIdPrefix}-bar`}
                className={`h-full transition-[width] duration-500 ease-out ${
                  isComplete
                    ? "bg-emerald-400"
                    : isPanelPaused
                    ? "bg-amber-400"
                    : "bg-gradient-to-r from-indigo-400 to-violet-400"
                }`}
                style={{ width: `${barPct}%` }}
              />
            </div>
          )}

          {/* Numeric pct (only during real upload work) */}
          {(phase === "uploading" || phase === "paused" || phase === "finalising") && (
            <p
              data-testid={`${testIdPrefix}-pct`}
              className="text-[10px] text-zinc-500 mt-1.5 tabular-nums"
            >
              {Math.round(barPct)}%
              {phase === "uploading" && typeof mbps === "number" && mbps > 0 && (
                <span
                  data-testid={`${testIdPrefix}-mbps`}
                  className="ml-2 text-zinc-500"
                >
                  · {mbps.toFixed(1)} Mbps
                </span>
              )}
            </p>
          )}

          {/* Slow-connection hint */}
          {showSlowHint && (
            <p
              data-testid={`${testIdPrefix}-slow-hint`}
              className="text-[11px] text-amber-300/90 mt-1.5"
            >
              Your connection is slow — the upload is still going.
            </p>
          )}

          {/* Paused — auto-retry countdown + manual override */}
          {isPanelPaused && (
            <div className="mt-3 space-y-2">
              {pausedSafe?.total ? (
                <p className="text-[11px] text-amber-200/90">
                  {pausedSafe.received}/{pausedSafe.total} chunks safe on the
                  server.
                </p>
              ) : null}
              {pausedReason !== "auth" && secsToRetry > 0 && (
                <p
                  data-testid={`${testIdPrefix}-auto-retry`}
                  className="text-[11px] text-amber-200/90"
                >
                  Auto-resuming in {secsToRetry}s…
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-1">
                {pausedReason !== "auth" && (
                  <button
                    data-testid={`${testIdPrefix}-resume-now`}
                    onClick={() => onResume?.()}
                    className="text-[11px] px-3 py-1.5 rounded-md bg-amber-400 hover:bg-amber-300 text-[#050505] font-extrabold"
                  >
                    Resume now
                  </button>
                )}
                {onCancel && (
                  <button
                    data-testid={`${testIdPrefix}-cancel`}
                    onClick={() => onCancel()}
                    className="text-[11px] px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 text-zinc-200 font-semibold inline-flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          {isError && onResume && (
            <button
              data-testid={
                canResume
                  ? `${testIdPrefix}-error-retry`
                  : `${testIdPrefix}-error-start-again`
              }
              onClick={() => onResume?.()}
              className="mt-2.5 text-[11px] px-3 py-1.5 rounded-md bg-rose-500 hover:bg-rose-400 text-white font-extrabold"
            >
              {canResume ? "Try again" : "Start again"}
            </button>
          )}
        </div>
      </div>

      {/* Inline keyframes — kept local so the panel is drop-in. */}
      <style>{`
        @keyframes upload-success {
          0%   { transform: scale(0.8); opacity: 0;   }
          40%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
        .animate-spin-slow {
          animation: spin 2.4s linear infinite;
        }
      `}</style>
    </div>
  );
}

/**
 * Helper: hold a phase for at least `minMs` before transitioning.
 * Spec §5: finalising must show for >= 1.5s so the user gets emotional
 * closure before "complete" flashes by.
 */
export function holdPhase(setPhase, nextPhase, minMs) {
  const t = setTimeout(() => setPhase(nextPhase), minMs);
  return () => clearTimeout(t);
}

/**
 * Helper: snap the final "Upload complete ✅" moment to a fixed
 * dwell time before the caller flips into the next screen. Returns
 * a cleanup function for useEffect cancellation.
 */
export function holdCompleteThen(callback, ms = COMPLETE_FLASH_MS) {
  const t = setTimeout(callback, ms);
  return () => clearTimeout(t);
}
